/**
 * Semantic Color Utilities
 *
 * Provides consistent color classes for status indicators, badges, and alerts
 * across the application. Uses the centralized color system from styles.css.
 *
 * Usage:
 * - Import the utility function you need
 * - Use in className with template literals or cn() utility
 *
 * Example:
 * <span className={cn(getStatusBadgeClasses('success'))}>Active</span>
 */

/**
 * Status types supported by the color system
 */
export type SemanticStatus = 'success' | 'warning' | 'info' | 'error' | 'neutral';

/**
 * Get background and text classes for a status badge
 * Uses semantic color tokens that work in both light and dark mode
 */
export const getStatusBadgeClasses = (status: SemanticStatus): string => {
  const classes: Record<SemanticStatus, string> = {
    success: 'bg-success/10 text-success-foreground',
    warning: 'bg-warning/10 text-warning-foreground',
    info: 'bg-info/10 text-info-foreground',
    error: 'bg-destructive/10 text-destructive',
    neutral: 'bg-muted text-muted-foreground',
  };
  return classes[status] || classes.neutral;
};

/**
 * Get border and background classes for an alert/notification box
 */
export const getAlertClasses = (status: SemanticStatus): string => {
  const classes: Record<SemanticStatus, string> = {
    success: 'border-success/30 bg-success/5 text-success-foreground',
    warning: 'border-warning/30 bg-warning/5 text-warning-foreground',
    info: 'border-info/30 bg-info/5 text-info-foreground',
    error: 'border-destructive/30 bg-destructive/5 text-destructive',
    neutral: 'border-border bg-muted/50 text-muted-foreground',
  };
  return classes[status] || classes.neutral;
};

/**
 * Get icon color class for a status
 */
export const getStatusIconClass = (status: SemanticStatus): string => {
  const classes: Record<SemanticStatus, string> = {
    success: 'text-success',
    warning: 'text-warning',
    info: 'text-info',
    error: 'text-destructive',
    neutral: 'text-muted-foreground',
  };
  return classes[status] || classes.neutral;
};

/**
 * Get background classes for a colored container/card
 */
export const getStatusBackgroundClasses = (status: SemanticStatus): string => {
  const classes: Record<SemanticStatus, string> = {
    success: 'bg-success/10',
    warning: 'bg-warning/10',
    info: 'bg-info/10',
    error: 'bg-destructive/10',
    neutral: 'bg-muted',
  };
  return classes[status] || classes.neutral;
};

/**
 * Get pill/tag classes for status indicators
 */
export const getStatusPillClasses = (status: SemanticStatus): string => {
  const classes: Record<SemanticStatus, string> = {
    success: 'bg-success text-white',
    warning: 'bg-warning text-warning-foreground',
    info: 'bg-info text-white',
    error: 'bg-destructive text-white',
    neutral: 'bg-secondary text-secondary-foreground',
  };
  return classes[status] || classes.neutral;
};

/**
 * Legacy color mappings - for gradual migration from hardcoded colors
 * Maps old Tailwind color classes to semantic equivalents
 *
 * Use this as a reference when migrating existing code:
 * - green-* -> success
 * - red-* -> error (or destructive for actions)
 * - yellow-* / amber-* -> warning
 * - blue-* -> info
 * - gray-* -> neutral / muted
 */
export const LEGACY_COLOR_MIGRATION = {
  // Success states (previously green)
  'bg-green-50': 'bg-success/10',
  'bg-green-100': 'bg-success/15',
  'bg-green-900/20': 'bg-success/20',
  'bg-green-900/30': 'bg-success/25',
  'text-green-600': 'text-success',
  'text-green-700': 'text-success-foreground',
  'text-green-800': 'text-success-foreground',
  'border-green-200': 'border-success/30',
  'border-green-800': 'border-success/40',

  // Error states (previously red)
  'bg-red-50': 'bg-destructive/10',
  'bg-red-100': 'bg-destructive/15',
  'bg-red-900/20': 'bg-destructive/20',
  'text-red-600': 'text-destructive',
  'text-red-700': 'text-destructive',
  'text-red-800': 'text-destructive',
  'border-red-200': 'border-destructive/30',
  'border-red-800': 'border-destructive/40',

  // Warning states (previously yellow)
  'bg-yellow-50': 'bg-warning/10',
  'bg-yellow-100': 'bg-warning/15',
  'bg-yellow-900/20': 'bg-warning/20',
  'text-yellow-600': 'text-warning',
  'text-yellow-700': 'text-warning-foreground',
  'text-yellow-800': 'text-warning-foreground',
  'border-yellow-200': 'border-warning/30',
  'border-yellow-800': 'border-warning/40',

  // Info states (previously blue)
  'bg-blue-50': 'bg-info/10',
  'bg-blue-100': 'bg-info/15',
  'bg-blue-900/20': 'bg-info/20',
  'bg-blue-950/20': 'bg-info/15',
  'text-blue-600': 'text-info',
  'text-blue-700': 'text-info-foreground',
  'text-blue-800': 'text-info-foreground',
  'text-blue-900': 'text-info-foreground',
  'border-blue-200': 'border-info/30',
  'border-blue-800': 'border-info/40',

  // Neutral states (previously gray)
  'bg-gray-50': 'bg-muted/50',
  'bg-gray-100': 'bg-muted',
  'bg-gray-200': 'bg-muted',
  'bg-gray-800': 'bg-muted',
  'bg-gray-900': 'bg-muted',
  'text-gray-500': 'text-muted-foreground',
  'text-gray-600': 'text-muted-foreground',
  'text-gray-700': 'text-foreground',
  'text-gray-800': 'text-foreground',
} as const;
